import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  READABLE_SUPPORTED_DATE_FORMATS,
  SUPPORTED_DATE_FORMATS,
  SUPPORTED_LANGUAGES,
  SUPPORTED_METRIC_UNITS,
  SUPPORTED_TIMEZONES,
} from '@app-core/constants/constants';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { DataService } from '@app-core/services/data/data.service';
@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit {
  public supportedTimeZones = SUPPORTED_TIMEZONES;
  public supportedMetricUnits = SUPPORTED_METRIC_UNITS;
  public supportedLanguages = SUPPORTED_LANGUAGES;
  public userSettingsForm: FormGroup;
  public isDirty$: Observable<boolean> = of(false);
  public selectedTimezone: string = '';
  private ngUnsubscribe = new Subject<void>();
  filteredTimezones: Observable<any[]>;
  public customOptions = {
    showLabel: true,
  };
  public currentDate: Date = new Date();

  constructor(
    public dialogRef: MatDialogRef<UserSettingsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private fb: FormBuilder,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService,
    public dataService: DataService
  ) {}

  public ngOnInit(): void {
    this.createForm();
    this.getSelectedFormatValue();
    this.getSelectedFormatKey();
  }

  public stripTime(format: string): string {
    if (!format) return '';
    return format.replace(/[\s:/.-]?(HH|mm|ss)/g, '').trim();
  }

  public sanitizeDateFormat(format: string): string {
    if (!format) return 'MM/DD/YYYY';
    return format.replace(/[\s:/.-]?(HH|mm|ss)/g, '').trim() || 'MM/DD/YYYY';
  }

  public get supportedDateFormatsToUse() {
    const isReadable = this.userSettingsForm?.get('isReadableDateFormat')?.value;
    return isReadable ? READABLE_SUPPORTED_DATE_FORMATS : SUPPORTED_DATE_FORMATS;
  }

  public getSelectedFormatValue(): string | undefined {
    const key = this.userSettingsForm.get('dateFormat')?.value;
    return this.supportedDateFormatsToUse.find((fmt) => fmt.key === key)?.key;
  }

  public getSelectedFormatKey(): string | undefined {
    const key = this.userSettingsForm.get('dateFormat')?.value;
    return this.supportedDateFormatsToUse.find((fmt) => fmt.key === key)?.value;
  }

  private createForm() {
    const {
      currentTimeZone = '',
      currentMetricUnit = '',
      currentDateFormat = '',
      isReadableDateFormat = true,
      currentLanguage = '',
    } = this.data || {};
    const matchedFormat = this.supportedDateFormatsToUse.find((fmt) => fmt.key === currentDateFormat || fmt.value === currentDateFormat);
    this.selectedTimezone = currentTimeZone;
    this.userSettingsForm = this.fb.group({
      timezone: this.fb.control(currentTimeZone, Validators.required),
      metricUnit: this.fb.control(currentMetricUnit, Validators.required),
      dateFormat: this.fb.control(matchedFormat ? matchedFormat.key : 'MM/DD/YYYY HH:mm:ss', Validators.required),
      isReadableDateFormat: this.fb.control(isReadableDateFormat, Validators.required),
      language: this.fb.control(currentLanguage, Validators.required),
    });

    this.formValueChanges();
    this.filteredTimezones = this.userSettingsForm.get('timezone').valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTimezones(value))
    );
  }

  private formValueChanges() {
    if (this.userSettingsForm) {
      this.isDirty$ = this.userSettingsForm.valueChanges.pipe(
        takeUntil(this.ngUnsubscribe),
        shareReplay(1),
        map(() => this.userSettingsForm.dirty)
      );
      this.isDirty$.subscribe();
    }
  }

  public onSubmit() {
    if (this.userSettingsForm.invalid) {
      return;
    }
    const oldTimeZone = this.data.currentTimeZone;
    const oldMetricUnit = this.data.currentMetricUnit;
    const oldDateFormat = this.data.currentDateFormat;
    const oldIsReadableDateFormat = this.data.isReadableDateFormat;
    const oldLanguage = this.data.currentLanguage;

    const { timezone, metricUnit, dateFormat, isReadableDateFormat, language } = this.userSettingsForm.getRawValue();

    const newTimeZone = timezone;
    const newMetricUnit = metricUnit;
    const newDateFormat = dateFormat;
    const newIsReadableDateFormat = isReadableDateFormat;
    const newLanguage = language;

    if (oldDateFormat !== newDateFormat) {
      this.gtmService.userPrefrenceChangeDateFormat(newDateFormat);
      this.mixpanelService.track('Change Date Format', {
        'Date Format': newDateFormat,
      });
    }
    if (oldIsReadableDateFormat !== newIsReadableDateFormat) {
      this.gtmService.userPrefrenceChangeViewDateFormat(newIsReadableDateFormat);
      this.mixpanelService.track('Change Date View Format', {
        'Date View Format': newIsReadableDateFormat ? 'Readble' : 'Normal',
      });
    }
    if (oldLanguage !== newLanguage) {
      this.gtmService.userPrefrenceChangeLanguageChange(newLanguage);
      this.mixpanelService.track('Change Language', {
        Language: newLanguage,
      });
    }
    if (oldMetricUnit !== newMetricUnit) {
      this.gtmService.userPrefrenceChangeMetricUnitChange(newMetricUnit);
      this.mixpanelService.track('Change Metric Unit', {
        'Metric Unit': newMetricUnit,
      });
    }
    if (oldTimeZone !== newTimeZone) {
      this.gtmService.userPrefrenceChangeTimeZoneChange(newTimeZone);
      this.mixpanelService.track('Change TimeZone', {
        'Time Zone': newTimeZone,
      });
    }

    this.onClose({
      timezone,
      metricUnit,
      dateFormat,
      isReadableDateFormat,
      language,
    });
  }

  private _filterTimezones(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.supportedTimeZones.filter((timezone) => timezone.key.toLowerCase().includes(filterValue));
  }

  clearTimezone() {
    this.userSettingsForm.get('timezone').setValue('');
  }

  public onClose(data: any) {
    this.dialogRef.close(data);
  }
  public onTimezoneSelect(selectedTimezone = '') {
    this.userSettingsForm.get('timezone').setValue(selectedTimezone);
    if (this.data) {
      this.data.currentTimeZone = selectedTimezone;
    }
  }

  public timezoneDisplayFn(timezoneValue: string): string {
    const timezoneOption = SUPPORTED_TIMEZONES.find((t) => t.value === timezoneValue);
    return timezoneOption?.customLabel || timezoneValue;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
